/**
 * MV4 — Structured two-line map caption.
 *
 * Replaces the run-on caption style (`Property Address: …,
 * Coordinates: …, — 32 imported layers`) with a primary (what) line
 * over a secondary (technical metadata) line. The primary line is
 * bold; the secondary line is muted. Same component services both
 * report maps so the hierarchy reads identically.
 *
 * Children of either slot can be plain strings or arrays of nodes —
 * the consumer decides whether to interleave commas or middle-dot
 * separators. This component just sets the typography and layout.
 *
 * The `secondary` slot is optional. When omitted only the primary
 * line renders, which is useful for sites with missing infrastructure
 * data that we'd otherwise pad with empty technical metadata.
 */
import React from 'react';

export interface MapCaptionProps {
  primary: React.ReactNode;
  secondary?: React.ReactNode;
  /**
   * Caller-supplied test hook so multiple captions in the same view
   * can be distinguished in tests without leaning on text matching.
   */
  testId?: string;
  /**
   * MV6 — drives whether interactive chrome paints. The caption itself
   * has no interactive affordances today, so this is currently used
   * only as a `data-surface-mode` attribute for future hover-tooltip
   * suppression; carrying the prop now keeps the API consistent across
   * map-surface components (sidebar, caption, chips all accept it).
   */
  surfaceMode?: 'interactive' | 'static';
}

export const MapCaption: React.FC<MapCaptionProps> = ({ primary, secondary, testId, surfaceMode = 'interactive' }) => (
  <div className="space-y-0.5" data-testid={testId} data-map-caption data-surface-mode={surfaceMode}>
    <div className="text-xs font-semibold text-text-primary leading-snug" data-caption-line="primary">
      {primary}
    </div>
    {secondary && (
      <div className="text-[11px] text-text-muted leading-snug" data-caption-line="secondary">
        {secondary}
      </div>
    )}
  </div>
);
